// frontend/src/components/RecordingDock.js

import React from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  LinearProgress,
  Chip,
  Fade,
  Tooltip
} from '@mui/material';
import {
  Stop,
  Pause,
  PlayArrow,
  Close,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
  CloudOff
} from '@mui/icons-material';
import { useRecording } from '../contexts/SimpleRecordingContext';

const RecordingDock = () => {
  const {
    isRecording,
    isPaused,
    recordingTime,
    maxDuration,
    planTier,
    connectionStatus,
    error,
    warning,
    uploadingChunks,
    failedChunks,
    showDock,
    stopRecording,
    pauseRecording,
    resumeRecording,
    setShowDock,
    clearError,
    clearWarning,
    resumeFailedUploads
  } = useRecording();
  
  if (!showDock) return null;
  
  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate progress
  const progress = (recordingTime * 1000 / maxDuration) * 100;
  const remainingTime = Math.max(0, (maxDuration / 1000) - recordingTime);
  const showCountdown = remainingTime < 60;
  
  // Connection status color
  const getConnectionColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'success';
      case 'reconnecting': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };
  
  return (
    <Fade in={showDock}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          width: 320,
          p: 2,
          zIndex: 9999,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: isRecording && !isPaused ? '#ff4444' : '#888',
                animation: isRecording && !isPaused ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 }
                }
              }}
            />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              Recording
            </Typography>
          </Box>
          
          <IconButton
            size="small"
            onClick={() => setShowDock(false)}
            sx={{ color: 'white' }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        
        {/* Timer and Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h4" sx={{ color: 'white', fontFamily: 'monospace' }}>
              {formatTime(recordingTime)}
            </Typography>
            
            {showCountdown && (
              <Chip
                label={`${Math.round(remainingTime)}s left`}
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: progress > 90 ? '#ff9800' : '#4caf50',
                borderRadius: 3
              }
            }}
          />
          
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
            {planTier === 'pro' ? 'Pro' : 'Free'} • Max {maxDuration / 60000} minutes
          </Typography>
        </Box>
        
        {/* Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          {isPaused ? (
            <IconButton
              onClick={resumeRecording}
              sx={{
                backgroundColor: 'white',
                color: '#667eea',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
              }}
            >
              <PlayArrow />
            </IconButton>
          ) : (
            <IconButton
              onClick={pauseRecording}
              sx={{
                backgroundColor: 'white',
                color: '#667eea',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
              }}
            >
              <Pause />
            </IconButton>
          )}
          
          <IconButton
            onClick={stopRecording}
            sx={{
              backgroundColor: '#ff4444',
              color: 'white',
              '&:hover': { backgroundColor: '#cc0000' }
            }}
          >
            <Stop />
          </IconButton>
        </Box>
        
        {/* Status Indicators */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Connection Status */}
          <Chip
            icon={
              connectionStatus === 'connected' ? <CheckCircle /> :
              connectionStatus === 'reconnecting' ? <CloudOff /> :
              <CloudOff />
            }
            label={connectionStatus}
            size="small"
            color={getConnectionColor()}
            variant="outlined"
            sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white' }}
          />
          
          {/* Upload Status */}
          {uploadingChunks > 0 && (
            <Chip
              icon={<CloudUpload />}
              label={`Uploading ${uploadingChunks}`}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
          
          {/* Failed Chunks */}
          {failedChunks > 0 && (
            <Tooltip title="Click to retry">
              <Chip
                icon={<ErrorIcon />}
                label={`Failed ${failedChunks}`}
                size="small"
                color="error"
                onClick={resumeFailedUploads}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
        </Box>
        
        {/* Warnings and Errors */}
        {warning && (
          <Box
            sx={{
              mt: 2,
              p: 1,
              backgroundColor: 'rgba(255,193,7,0.2)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Warning sx={{ color: '#ffc107', fontSize: 18 }} />
            <Typography variant="caption" sx={{ color: 'white' }}>
              {warning}
            </Typography>
          </Box>
        )}
        
        {error && (
          <Box
            sx={{
              mt: 2,
              p: 1,
              backgroundColor: 'rgba(244,67,54,0.2)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <ErrorIcon sx={{ color: '#f44336', fontSize: 18 }} />
            <Typography variant="caption" sx={{ color: 'white' }}>
              {error}
            </Typography>
            <IconButton
              size="small"
              onClick={clearError}
              sx={{ color: 'white', ml: 'auto' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Fade>
  );
};

export default RecordingDock;